import { create } from 'zustand'
import type { UserProfile } from '../types/module.types'

/**
 * Auth store — manages authentication state in memory.
 *
 * Why Zustand for auth state (not just React Context)?
 * The Axios client (outside the React tree) needs to read the access token
 * on every request. Zustand's getState() works outside React components,
 * making it the right tool for this cross-cutting concern.
 *
 * Security: Access token is stored in memory (not localStorage/sessionStorage).
 * It is cleared when the page is refreshed — the silent refresh mechanism
 * re-acquires it automatically from the HttpOnly refresh token cookie.
 */
interface AuthState {
  accessToken: string | null
  user: UserProfile | null
  isAuthenticated: boolean

  setAuth: (token: string, user: UserProfile) => void
  setAccessToken: (token: string) => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  isAuthenticated: false,

  setAuth: (token, user) =>
    set({ accessToken: token, user, isAuthenticated: true }),

  setAccessToken: (token) =>
    set({ accessToken: token }),

  clearAuth: () =>
    set({ accessToken: null, user: null, isAuthenticated: false }),
}))
